import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";
import PublicLayout from "../components/PublicLayout";


const BUSINESS_TYPES = [
  "Courier / Delivery Company",
  "Supermarket / Grocery",
  "Pharmacy / Chemist",
  "Restaurant / Food Delivery",
  "E-commerce / Online Shop",
  "Wholesale / Distribution",
  "Logistics Company",
  "Retail Shop",
  "Other",
];


const PLANS = [
  { id: "starter", name: "Starter", price: "KES 999/mo", drivers: "Up to 3 drivers" },
  { id: "growth", name: "Growth", price: "KES 2,499/mo", drivers: "Up to 10 drivers" },
  { id: "enterprise", name: "Enterprise", price: "KES 5,999/mo", drivers: "Unlimited drivers" },
];


export default function RegisterPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();


  const [step, setStep] = useState(1);
  const [selectedPlan, setSelectedPlan] = useState(() => {
    const planFromUrl = searchParams.get("plan");
    return ["starter", "growth", "enterprise"].includes(planFromUrl)
      ? planFromUrl
      : "starter";
  });


  const [form, setForm] = useState({
    shop_name: "",
    business_type: "",
    address: "",
    registration_number: "",
    kra_pin: "",
    name: "",
    phone: "",
    email: "",
    password: "",
    password_confirmation: "",
  });

  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);


  // Step 3 – OTP verification
  const [otpCode, setOtpCode] = useState("");
  const [otpError, setOtpError] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [pendingAuth, setPendingAuth] = useState(null);
  
  // KRA PIN validation state
  const [kraPinError, setKraPinError] = useState("");
  
  const set = (key, val) => setForm((prev) => ({ ...prev, [key]: val }));


  // KRA PIN format validation
  const validateKraPin = (pin) => {
    if (!pin) return null;
    return /^[A-Z]\d{9}[A-Z]$/i.test(pin)
      ? null
      : "KRA PIN must be in format A123456789B (letter, 9 digits, letter)";
  };


  const STEPS = [
    { n: 1, label: "Business Info" },
    { n: 2, label: "Account Setup" },
    { n: 3, label: "Verify Phone" },
  ];


  const handleNext = (e) => {
    e.preventDefault();
    setError("");


    if (!form.shop_name || !form.business_type || !form.address) {
      setError("Please fill in all required fields.");
      return;
    }


    if (!form.registration_number && !form.kra_pin) {
      setError("Please provide at least your KRA PIN or Business Registration Number.");
      return;
    }


    // Validate KRA PIN if filled
    if (form.kra_pin && validateKraPin(form.kra_pin)) {
      setError("Please enter a valid KRA PIN format.");
      return;
    }


    setStep(2);
  };


  const handleCreateAccount = async (e) => {
    e.preventDefault();
    setError("");


    if (!form.email) {
      setError("Email address is required for account notifications.");
      return;
    }
    if (form.password !== form.password_confirmation) {
      setError("Passwords do not match.");
      return;
    }
    if (!agreedToTerms) {
      setError("You must agree to the terms and conditions to continue.");
      return;
    }


    setLoading(true);
    try {
      const payload = {
        shop_name:             form.shop_name,
        business_type:         form.business_type,
        address:               form.address,
        registration_number:   form.registration_number || null,
        kra_pin:               form.kra_pin || null,
        name:                  form.name,
        phone:                 form.phone,
        email:                 form.email,
        password:              form.password,
        password_confirmation: form.password_confirmation,
        plan:                  selectedPlan,
        agreed_to_terms:       agreedToTerms,
      };


      const res = await api.post("/register", payload);


      // ✅ Store token temporarily — do NOT call login() yet
      setPendingAuth({ token: res.data.token, user: res.data.user });


      // Send OTP
      await api.post("/otp/send", { phone: form.phone });
      setStep(3);
    } catch (err) {
      setError(
        err.response?.data?.message ||
        Object.values(err.response?.data?.errors || {}).flat().join(" ") ||
        "Registration failed. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };


  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setOtpError("");
    setOtpLoading(true);
    try {
      await api.post("/otp/verify", { phone: form.phone, code: otpCode });


      if (pendingAuth) {
        login(pendingAuth.token, pendingAuth.user);
      }
      navigate("/pending-approval");
    } catch (err) {
      setOtpError(err.response?.data?.message || "Invalid OTP. Try again.");
    } finally {
      setOtpLoading(false);
    }
  };


  const handleResendOtp = async () => {
    setResending(true);
    try {
      await api.post("/otp/send", { phone: form.phone });
      setOtpError("");
    } finally {
      setResending(false);
    }
  };


  return (
    <PublicLayout>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=DM+Sans:wght@400;500;600&display=swap');`}</style>


      {/* Welcome Banner */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 pt-32 pb-16 text-center px-5 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              "radial-gradient(circle at 70% 50%, rgba(249,115,22,0.6) 0%, transparent 60%)",
          }}
        />
        <div className="relative">
          <div className="inline-flex items-center gap-2 bg-orange-500/15 border border-orange-500/20 text-orange-400 text-xs font-semibold px-4 py-1.5 rounded-full mb-5">
            🎉 7-Day Free Trial — No Credit Card Required
          </div>
          <h1
            className="text-4xl md:text-5xl font-extrabold text-white mb-3"
            style={{ fontFamily: "'Sora', sans-serif" }}
          >
            Welcome to{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-300">
              TumaTrack
            </span>
          </h1>
          <p className="text-slate-400 text-base max-w-md mx-auto">
            Register your business and start tracking deliveries in real time.
          </p>
        </div>
      </section>


      {/* Form */}
      <section className="bg-slate-50 py-12 px-5">
        <div className="max-w-lg mx-auto">
          <div className="flex flex-wrap justify-center gap-5 mb-6 text-xs text-slate-500">
            {["✅ Free for 7 days", "🔒 Verified businesses only", "💚 M-Pesa billing"].map((b) => (
              <span key={b}>{b}</span>
            ))}
          </div>


          <div className="flex items-center gap-3 mb-6">
            {STEPS.map((s) => (
              <div key={s.n} className="flex items-center gap-3 flex-1">
                <div className={`flex items-center gap-2 flex-1 justify-end`}>
                  <div
                    className={`h-0.5 flex-1 rounded ${
                      step >= s.n ? "bg-orange-400" : "bg-gray-200"
                    }`}
                  />
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 transition-all ${
                      step >= s.n ? "bg-orange-500 text-white" : "bg-gray-200 text-gray-400"
                    }`}
                  >
                    {step > s.n ? "✓" : s.n}
                  </div>
                  <div
                    className={`h-0.5 flex-1 rounded ${
                      step > s.n ? "bg-orange-400" : "bg-gray-200"
                    }`}
                  />
                </div>
              </div>
            ))}
          </div>


          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8">
            {error && (
              <div className="mb-5 flex items-start gap-3 bg-red-50 border border-red-100 text-red-700 text-sm rounded-xl p-4">
                <svg
                  className="w-4 h-4 mt-0.5 shrink-0"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 9v3.75m9.303 3.376c-.866 1.5.217 3.374-1.948 3.374H4.645c-1.73 0-2.813-1.874-1.948-3.374L10.051 3.378c.866-1.5 3.032-1.5 3.898 0L21.303 16.126zM12 15.75h.007v.008H12v-.008z"
                  />
                </svg>
                {error}
              </div>
            )}


            {step === 1 && (
              <form onSubmit={handleNext} className="space-y-4">
                <h2
                  className="text-xl font-bold text-slate-900 mb-6"
                  style={{ fontFamily: "'Sora', sans-serif" }}
                >
                  Business Information
                </h2>


                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                    Business Name <span className="text-red-400">*</span>
                  </label>
                  <input
                    value={form.shop_name}
                    onChange={(e) => set("shop_name", e.target.value)}
                    required
                    placeholder="e.g. Mama Mboga Deliveries"
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                  />
                </div>


                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                    Business Type <span className="text-red-400">*</span>
                  </label>
                  <select
                    value={form.business_type}
                    onChange={(e) => set("business_type", e.target.value)}
                    required
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent bg-white"
                  >
                    <option value="">Select business type...</option>
                    {BUSINESS_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>


                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                    Business Location / Address <span className="text-red-400">*</span>
                  </label>
                  <input
                    value={form.address}
                    onChange={(e) => set("address", e.target.value)}
                    required
                    placeholder="e.g. Westlands, Nairobi"
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                  />
                </div>


                <div className="border-t border-slate-100 pt-4">
                  <p className="text-xs text-slate-400 mb-4 font-medium">
                    Provide at least one of the following for verification{" "}
                    <span className="text-red-400">*</span>
                  </p>


                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                        Registration No. <span className="text-red-400">*</span>
                      </label>
                      <input
                        value={form.registration_number}
                        onChange={(e) => set("registration_number", e.target.value)}
                        placeholder="BN/XXXXXXXX"
                        className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                      />
                    </div>


                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                        KRA PIN <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        value={form.kra_pin}
                        onChange={(e) => {
                          const val = e.target.value.toUpperCase();
                          set("kra_pin", val);
                          setKraPinError(validateKraPin(val) || "");
                        }}
                        placeholder="e.g. P05******7X"
                        maxLength={11}
                        className={`w-full px-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent font-mono ${
                          kraPinError ? "border-red-300 bg-red-50" : "border-slate-200"
                        }`}
                      />
                      {kraPinError && (
                        <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1">
                          <span>⚠️</span> {kraPinError}
                        </p>
                      )}
                      {form.kra_pin && !kraPinError && (
                        <p className="text-green-500 text-xs mt-1.5 flex items-center gap-1">
                          <span>✅</span> Valid KRA PIN format
                        </p>
                      )}
                    </div>
                  </div>
                </div>


                <div className="border-t border-slate-100 pt-4">
                  <p className="text-xs text-slate-400 mb-4 font-medium">
                    Select your pricing plan
                  </p>


                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {PLANS.map((plan) => (
                      <button
                        key={plan.id}
                        type="button"
                        onClick={() => setSelectedPlan(plan.id)}
                        className={`p-4 rounded-xl border text-left transition-all ${
                          selectedPlan === plan.id
                            ? "border-orange-500 bg-orange-50"
                            : "border-slate-200 bg-white hover:border-orange-300"
                        }`}
                      >
                        <div className="font-semibold text-slate-900">{plan.name}</div>
                        <div className="text-sm text-orange-500 font-medium">{plan.price}</div>
                        <div className="text-xs text-slate-400 mt-0.5">{plan.drivers}</div>
                      </button>
                    ))}
                  </div>
                </div>


                <button
                  type="submit"
                  disabled={
                    (form.kra_pin && !!validateKraPin(form.kra_pin)) ||
                    (!form.kra_pin && !form.registration_number)
                  }
                  className="w-full mt-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-xl shadow-md shadow-orange-200 transition-all flex items-center justify-center gap-2"
                >
                  Continue to Account Setup
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2.5}
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
                    />
                  </svg>
                </button>
              </form>
            )}


            {step === 2 && (
              <form onSubmit={handleCreateAccount} className="space-y-4">
                <div className="flex items-center gap-3 mb-6">
                  <button
                    type="button"
                    onClick={() => {
                      setStep(1);
                      setError("");
                    }}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"
                      />
                    </svg>
                  </button>
                  <h2
                    className="text-xl font-bold text-slate-900"
                    style={{ fontFamily: "'Sora', sans-serif" }}
                  >
                    Account Setup
                  </h2>
                </div>


                <div className="bg-orange-50 border border-orange-100 rounded-xl p-4 mb-2">
                  <p className="text-xs text-orange-600 font-semibold uppercase tracking-wide mb-1">
                    Business
                  </p>
                  <p className="font-bold text-gray-900">{form.shop_name}</p>
                  <p className="text-gray-500 text-xs">
                    {form.business_type} · {form.address}
                  </p>
                  <p className="text-gray-500 text-xs mt-1">
                    Plan: {selectedPlan}
                  </p>
                </div>


                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                    Your Full Name <span className="text-red-400">*</span>
                  </label>
                  <input
                    value={form.name}
                    onChange={(e) => set("name", e.target.value)}
                    required
                    placeholder="e.g. Jane Wanjiru"
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                  />
                </div>


                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                    Phone Number <span className="text-red-400">*</span>
                  </label>
                  <input
                    value={form.phone}
                    onChange={(e) => set("phone", e.target.value)}
                    required
                    placeholder="07XXXXXXXX"
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                  />
                </div>


                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                    Email Address <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => set("email", e.target.value)}
                    required
                    placeholder="you@example.com"
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                  />
                </div>


                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                    Password <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="password"
                    value={form.password}
                    onChange={(e) => set("password", e.target.value)}
                    required
                    placeholder="Min. 8 characters"
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                  />
                </div>


                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                    Confirm Password <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="password"
                    value={form.password_confirmation}
                    onChange={(e) => set("password_confirmation", e.target.value)}
                    required
                    placeholder="Repeat your password"
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                  />
                </div>


                {/* Terms & Conditions Checkbox */}
                <div className="flex items-start gap-3 p-4 bg-orange-50 border border-orange-100 rounded-xl">
                  <input
                    type="checkbox"
                    id="terms"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    className="mt-0.5 w-4 h-4 accent-orange-500 cursor-pointer shrink-0"
                  />
                  <label htmlFor="terms" className="text-sm text-gray-600 cursor-pointer leading-relaxed">
                    I have read and agree to the{" "}
                    <a
                      href="https://tumatrack.vercel.app/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-orange-500 font-semibold hover:text-orange-600 underline"
                    >
                      Terms and Conditions
                    </a>
                    {" "}of TumaTrack. I confirm that all business information provided is accurate and complete.
                  </label>
                </div>


                {/* Submit button — disable until checkbox ticked */}
                <button
                  type="submit"
                  disabled={!agreedToTerms || loading}
                  className="w-full py-3 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl font-semibold transition-colors"
                >
                  {loading ? "Creating Account..." : "Create Account"}
                </button>
              </form>
            )}


            {step === 3 && (
              <form onSubmit={handleVerifyOtp} className="space-y-5">
                <div className="text-center mb-6">
                  <div className="w-14 h-14 bg-orange-100 rounded-2xl flex items-center justify-center text-2xl mx-auto mb-4">
                    📱
                  </div>
                  <h2
                    className="text-xl font-bold text-slate-900"
                    style={{ fontFamily: "'Sora', sans-serif" }}
                  >
                    Verify your phone
                  </h2>
                  <p className="text-slate-500 text-sm mt-2">
                    We sent a 6-digit code to{" "}
                    <span className="font-semibold text-slate-700">{form.phone}</span>
                  </p>
                </div>


                {otpError && (
                  <div className="bg-red-50 border border-red-100 text-red-700 text-sm rounded-xl p-4">
                    {otpError}
                  </div>
                )}


                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                    Verification Code
                  </label>
                  <input
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    required
                    maxLength={6}
                    placeholder="000000"
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl text-2xl font-mono text-center tracking-[0.5em] focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                  />
                </div>


                <button
                  type="submit"
                  disabled={otpLoading || otpCode.length !== 6}
                  className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-semibold py-3 rounded-xl transition-all"
                >
                  {otpLoading ? "Verifying..." : "Verify & Continue"}
                </button>


                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={resending}
                  className="w-full text-sm text-slate-500 hover:text-orange-500 transition-colors"
                >
                  {resending ? "Sending..." : "Didn't receive it? Resend code"}
                </button>
              </form>
            )}


            <p className="mt-5 text-center text-sm text-slate-500">
              Already have an account?{" "}
              <Link to="/login" className="text-orange-500 hover:text-orange-600 font-semibold transition-colors">
                Log in
              </Link>
            </p>
          </div>


          <p className="text-center text-xs text-slate-400 mt-5">
            By registering you agree to our Terms of Service. All businesses are verified before activation.
          </p>
        </div>
      </section>
    </PublicLayout>
  );
}